// Required Google Workspace scopes
export const SCOPES = [
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/drive.file'
];

let cachedAccessToken: string | null = null;
if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
  cachedAccessToken = sessionStorage.getItem('sheets_access_token');
}
let isSigningIn = false;

export const getSheetsAccessToken = (): string | null => cachedAccessToken;

export const setSheetsAccessToken = (token: string | null) => {
  cachedAccessToken = token;
  if (typeof window !== 'undefined') {
    if (token) {
      sessionStorage.setItem('sheets_access_token', token);
    } else {
      sessionStorage.removeItem('sheets_access_token');
    }
  }
};

export const initSheetsAuth = async (
  onSuccess?: (user: any, token: string) => void,
  onFailure?: () => void
) => {
  try {
    const { auth } = await import('./firebase');
    const { onAuthStateChanged } = await import('firebase/auth');
    return onAuthStateChanged(auth, async (user: any) => {
      if (user && cachedAccessToken) {
        if (onSuccess) onSuccess(user, cachedAccessToken);
      } else {
        if (!isSigningIn) {
          if (onFailure) onFailure();
        }
      }
    });
  } catch (err) {
    if (onFailure) onFailure();
  }
};

export const googleSheetsSignIn = async (): Promise<{ user: any; accessToken: string }> => {
  try {
    isSigningIn = true;
    const { auth } = await import('./firebase');
    const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
    const sheetsProvider = new GoogleAuthProvider();
    SCOPES.forEach(scope => sheetsProvider.addScope(scope));
    sheetsProvider.setCustomParameters({
      prompt: 'consent',
      access_type: 'offline'
    });
    const result = await signInWithPopup(auth, sheetsProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (!credential?.accessToken) {
      throw new Error('No access token returned from Google Sign-In.');
    }
    cachedAccessToken = credential.accessToken;
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('sheets_access_token', cachedAccessToken);
    }
    return { user: result.user, accessToken: cachedAccessToken };
  } catch (error) {
    console.error('Google Sheets Sign-In error:', error);
    throw error;
  } finally {
    isSigningIn = false;
  }
};

/**
 * Creates a brand new Google Spreadsheet with two preconfigured tabs:
 * 1. "Form Submissions"
 * 2. "Link & Button Clicks"
 */
export const createTrackingSpreadsheet = async (
  title = 'FIVE LENS - Website Forms & Links Tracking',
  token?: string
): Promise<{ spreadsheetId: string; spreadsheetUrl: string }> => {
  const accessToken = token || cachedAccessToken;
  if (!accessToken) {
    throw new Error('Google Sheets access token missing. Please sign in with Google.');
  }

  const payload = {
    properties: {
      title
    },
    sheets: [
      {
        properties: {
          title: 'Form Submissions',
          gridProperties: {
            frozenRowCount: 1
          }
        },
        data: [
          {
            startRow: 0,
            startColumn: 0,
            rowData: [
              {
                values: [
                  { userEnteredValue: { stringValue: 'Timestamp (UTC)' } },
                  { userEnteredValue: { stringValue: 'Form Name' } },
                  { userEnteredValue: { stringValue: 'Page Location' } },
                  { userEnteredValue: { stringValue: 'Email / Contact' } },
                  { userEnteredValue: { stringValue: 'Details / Payload' } },
                  { userEnteredValue: { stringValue: 'CTA Position' } },
                  { userEnteredValue: { stringValue: 'Device / Context' } }
                ]
              }
            ]
          }
        ]
      },
      {
        properties: {
          title: 'Link & Button Clicks',
          gridProperties: {
            frozenRowCount: 1
          }
        },
        data: [
          {
            startRow: 0,
            startColumn: 0,
            rowData: [
              {
                values: [
                  { userEnteredValue: { stringValue: 'Timestamp (UTC)' } },
                  { userEnteredValue: { stringValue: 'Action Type' } },
                  { userEnteredValue: { stringValue: 'Button / Link Text' } },
                  { userEnteredValue: { stringValue: 'Target URL' } },
                  { userEnteredValue: { stringValue: 'Page Location' } },
                  { userEnteredValue: { stringValue: 'CTA Position' } },
                  { userEnteredValue: { stringValue: 'Extra Info' } }
                ]
              }
            ]
          }
        ]
      }
    ]
  };

  const res = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.error?.message || `Failed to create spreadsheet (${res.status})`);
  }

  const data = await res.json();
  return {
    spreadsheetId: data.spreadsheetId,
    spreadsheetUrl: data.spreadsheetUrl || `https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit`
  };
};

/**
 * Appends a row of values to a specific sheet tab in a Google Spreadsheet.
 */
export const appendRowToSheet = async (
  spreadsheetId: string,
  sheetName: string,
  rowValues: (string | number | boolean | null | undefined)[],
  token?: string
): Promise<any> => {
  const accessToken = token || cachedAccessToken;
  if (!accessToken) {
    throw new Error('Google Sheets access token missing. Please sign in with Google.');
  }

  const cleanValues = rowValues.map(v => (v === null || v === undefined ? '' : String(v)));
  const range = `${sheetName}!A:Z`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(
    range
  )}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      values: [cleanValues]
    })
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.error?.message || `Failed to append row (${res.status})`);
  }

  return await res.json();
};

/**
 * Batch appends multiple rows of values to a specific sheet tab.
 */
export const batchAppendRowsToSheet = async (
  spreadsheetId: string,
  sheetName: string,
  rows: (string | number | boolean | null | undefined)[][],
  token?: string
): Promise<any> => {
  const accessToken = token || cachedAccessToken;
  if (!accessToken) {
    throw new Error('Google Sheets access token missing. Please sign in with Google.');
  }
  if (!rows.length) return null;

  const cleanRows = rows.map(r => r.map(v => (v === null || v === undefined ? '' : String(v))));
  const range = `${sheetName}!A:Z`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(
    range
  )}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      values: cleanRows
    })
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.error?.message || `Failed to batch append rows (${res.status})`);
  }

  return await res.json();
};

/**
 * Reads row values from a spreadsheet tab.
 */
export const fetchSheetValues = async (
  spreadsheetId: string,
  sheetName: string,
  range = 'A1:Z500',
  token?: string
): Promise<string[][]> => {
  const accessToken = token || cachedAccessToken;
  if (!accessToken) {
    throw new Error('Google Sheets access token missing. Please sign in with Google.');
  }

  const queryRange = `${sheetName}!${range}`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(queryRange)}`;

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Failed to fetch sheet values (${res.status})`);
  }

  const data = await res.json();
  return data.values || [];
};

/**
 * Fetches spreadsheet metadata (title, sheets list).
 */
export const fetchSpreadsheetInfo = async (
  spreadsheetId: string,
  token?: string
): Promise<{ title: string; sheets: string[]; url: string }> => {
  const accessToken = token || cachedAccessToken;
  if (!accessToken) {
    throw new Error('Google Sheets access token missing. Please sign in with Google.');
  }

  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=properties.title,sheets.properties.title`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Failed to fetch spreadsheet info (${res.status})`);
  }

  const data = await res.json();
  const sheets = (data.sheets || []).map((s: any) => s.properties?.title || '');
  return {
    title: data.properties?.title || 'Untitled Spreadsheet',
    sheets,
    url: `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`
  };
};

export const ensureTrackingTabsExist = async (spreadsheetId: string, token?: string) => {
  const accessToken = token || cachedAccessToken;
  if (!accessToken) throw new Error('Google Sheets access token missing.');
  
  const info = await fetchSpreadsheetInfo(spreadsheetId, accessToken);
  const existingSheets = info.sheets;
  
  const missingTabs: string[] = [];
  if (!existingSheets.includes('Form Submissions')) missingTabs.push('Form Submissions');
  if (!existingSheets.includes('Link & Button Clicks')) missingTabs.push('Link & Button Clicks');
  
  if (missingTabs.length === 0) return;
  
  const requests = missingTabs.map(title => ({
    addSheet: {
      properties: {
        title,
        gridProperties: { frozenRowCount: 1 }
      }
    }
  }));
  
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ requests })
  });
  
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || 'Failed to create missing tabs');
  }
  
  if (missingTabs.includes('Form Submissions')) {
    await appendRowToSheet(spreadsheetId, 'Form Submissions', [
      'Timestamp (UTC)', 'Form Name', 'Page Location', 'Email / Contact', 'Details / Payload', 'CTA Position', 'Device / Context'
    ], accessToken);
  }
  if (missingTabs.includes('Link & Button Clicks')) {
    await appendRowToSheet(spreadsheetId, 'Link & Button Clicks', [
      'Timestamp (UTC)', 'Event Name', 'Label / Text', 'Target URL', 'Page Location', 'CTA Position', 'Details'
    ], accessToken);
  }
};
